import {
  CompanyEscalationRecipient,
  TriageRecipient,
} from 'data/api/triage.api';
import { getEscalationCompany, getEscalationUsers } from 'data/api/company.api';
import { getUserDetail } from 'data/api/user.api';

export interface Person {
  userId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

export const byUserId = (people: Person[]): Person[] => {
  const seen = new Set<string>();
  return people.filter((person) => {
    if (!person?.userId || seen.has(person.userId)) return false;
    seen.add(person.userId);
    return true;
  });
};

export interface EscalationContacts {
  /** The company's admin roster. */
  roster: Person[];
  /** The contacts configured per triage category, which seed To and Cc. */
  recipients: CompanyEscalationRecipient[];
}

// The inbox row carries the company's *name*, not its id, and the escalation
// routes are keyed by id — so the member record is what resolves it. Read on
// demand rather than per selected conversation: nothing else in the rail needs
// it, and arrowing down the queue must not spend a request per row.
export const loadEscalationContacts = async (
  userId: string,
): Promise<EscalationContacts> => {
  const user = await getUserDetail(userId);
  const companyId = user?.user?.companyId;
  if (!companyId) throw Error('NO COMPANY');
  const [users, escalation] = await Promise.all([
    getEscalationUsers(companyId),
    getEscalationCompany(companyId),
  ]);
  return {
    roster: users?.users || [],
    // CompanyResponse wraps the row, so the recipients are one level in. Read
    // off the top level they are always undefined, and every Type silently
    // seeds an empty To.
    recipients: escalation?.company?.recipients || [],
  };
};

// Everyone a recipient picker may offer: the admin roster, anyone configured
// as an escalation contact, and anyone the triage already addresses. Eight
// production contacts have no company link at all (collab #76) — they are
// legitimate defaults and must render even though the roster lacks them.
export const pickablePeople = (
  contacts: EscalationContacts | undefined,
  current: TriageRecipient[] = [],
): Person[] =>
  byUserId([
    ...current,
    ...(contacts?.roster || []),
    ...(contacts?.recipients || []).map((entry) => entry.user),
  ]);
